<template>
    <div>
        <rkm-area
            type="fieldset"
            title="Filtros"
        >
            <rkm-form ref="Form">
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="tiposCota"
                            label="Tipo de cota"
                            text-field="nome"
                            v-model="model.tipoCotaId"
                            value-field="id"
                            :validations="validations.tipoCotaId"
                            @change="handleTipoCota()"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            range
                            right
                            :range-inline="false"
                            start-label="Período de"
                            end-label="Até"
                            v-model="model.periodo"
                            :start-validations="validations.periodo.start"
                            :end-validations="validations.periodo.end"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row
                    v-if="isLaboratorioVisible"
                    class="flex-nowrap"
                >
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="unidadeLaboratorio"
                            label="Laboratório"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.laboratorioId"
                            default-option-text="Todos"
                            value-field="unidadeId"
                            @change="loadFields()"
                        />
                    </rkm-col>

                    <rkm-col class="flex-grow-1">
                        <rkm-form-select
                            label="Local de atendimento"
                            :options="laboratorioInfraestruturas"
                            textField="nome"
                            v-model="model.laboratorioInfraestruturaId"
                            valueField="id"
                            default-option-text="Todos"
                            show-default-option
                            @change="loadFields()"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="isUnidadeVisible">
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="unidadeSolicitante"
                            label="Unidade solicitante"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="unidadeId"
                            default-option-text="Todas"
                            @change="loadFields()"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            label="Local de atendimento"
                            :options="infraestruturas"
                            textField="nome"
                            v-model="model.infraestruturaId"
                            valueField="id"
                            default-option-text="Todos"
                            show-default-option
                            @change="loadFields()"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="isExameVisible">
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="agendamentoExame"
                            label="Exame"
                            show-default-option
                            text-field="nome"
                            v-model="model.exameId"
                            value-field="id"
                            default-option-text="Todos"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            color="primary"
                            @click="$refs.Form.validate(search)"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="resetForm()"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            v-if="itemsNotSave.length > 0"
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                    menu-class="auto-scroll"
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>
                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>
                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                    :disabled="itemsNotSave.length === 0"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itemsNotSave.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :items="itemsNotSave"
                :fields="fields"
                hide-filters
                sort-by="prestadorNome"
                :page-options="[10, 25, 50, 100]"
            >
                <template v-slot:cell(cotaValor)="row">
                    {{ row.item.cotaValor === null ? '-' : `R$ ${formatMoney(row.item.cotaValor, 2)}` }}
                </template>

                <template v-slot:cell(valorConsumido)="row">
                    R$ {{ formatMoney(row.item.valorConsumido, 2) }}
                </template>

                <template v-slot:cell(cotaQuantidade)="row">
                    {{ row.item.cotaQuantidade === null ? '-' : row.item.cotaQuantidade }}
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            v-if="itemsNotSave.length > 0"
            no-margin-top
            title="Totalizadores"
        >
            <rkm-table
                :items="totalizadores"
                :fields="totaisFields"
                hide-filters
                hide-pagination
                :tbody-tr-class="getTotalRowClass"
            >
                <template v-slot:cell(cotaValor)="row">
                    {{ row.item.cotaValor === null ? '-' : `R$ ${formatMoney(row.item.cotaValor, 2)}` }}
                </template>

                <template v-slot:cell(valorConsumido)="row">
                    R$ {{ formatMoney(row.item.valorConsumido, 2) }}
                </template>

                <template v-slot:cell(cotaQuantidade)="row">
                    {{ row.item.cotaQuantidade === null ? '-' : row.item.cotaQuantidade }}
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, object, formats, data, stringCase, file } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import origemCota from '@/enums/exame-laboratorial/origemCota'

// Filters.
import format from '@/filters/formats'

export default {
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false) && c.key !== 'procedimentoCodigo' && c.key !== 'exameNome')
        },
        fields() {

            return this.columns.filter(c => {

                if (c.key === 'procedimentoCodigo' || c.key === 'exameNome') {

                    return this.model.tipoCotaId === this.enums.origemCota.EXAME
                }

                return (c.fixed || false) || this.selectedCols.includes(c.key)
            })
        },
        totaisFields() {

            return [
                { key: 'prestadorNome', label: 'Prestador' },
                { key: 'cotaValor', label: 'Valor da cota', class: 'col-min text-center' },
                { key: 'valorConsumido', label: 'Valor consumido', class: 'col-min text-center' },
                { key: 'cotaQuantidade', label: 'Quantidade da cota', class: 'col-min text-center' },
                { key: 'quantidadeConsumida', label: 'Quantidade consumida', class: 'col-min text-center' },
            ]
        },
        totalizadores() {

            return [...this.totais.prestadores, this.totais.geral]
        },
        validations() {

            return {
                tipoCotaId: { required: true },
                periodo: {
                    start: { required: true },
                    end: { required: true },
                },
            }
        },
        isLaboratorioVisible() {

            return [this.enums.origemCota.EXAME, this.enums.origemCota.LABORATORIO].includes(this.model.tipoCotaId)
        },
        isUnidadeVisible() {

            return [this.enums.origemCota.LABORATORIO, this.enums.origemCota.UNIDADE_SOLICITANTE].includes(this.model.tipoCotaId)
        },
        isExameVisible() {

            return this.model.tipoCotaId === this.enums.origemCota.EXAME
        },
        infraestruturas() {

            if (this.model.unidadeId) {

                let infraestruturaPrincipal = this.unidadeSolicitante.find(u => u.unidadeId == this.model.unidadeId).infraestruturas

                return infraestruturaPrincipal
            }

            return []
        },
        laboratorioInfraestruturas() {

            if (this.model.laboratorioId) {

                let infraestruturaPrincipal = this.unidadeLaboratorio.find(u => u.unidadeId == this.model.laboratorioId).infraestruturas

                return infraestruturaPrincipal
            }

            return []
        },
    },
    created() {

        this.repositories = {
            exame: this.getConectaSusRepository('exameLaboratorial.relatorioCotaConsumo'),
            impressao: this.getConectaSusRepository('impressao.exameLaboratorial'),
            exportacao: this.getConectaSusRepository('exportacao.exameLaboratorial'),
        },

        this.load();
    },
    data() {
        return {
            model: {
                tipoCotaId: null,
                unidadeId: null,
                infraestruturaId: null,
                laboratorioId: null,
                laboratorioInfraestruturaId: null,
                exameId: null,
                exameNome: null,
                periodo: {
                    start: null,
                    end: null,
                },
            },
            tiposCota: [],
            agendamentoExame: [],
            unidadeSolicitante: [],
            unidadeLaboratorio: [],
            itemsNotSave: [],
            totais: {
                prestadores: [],
                geral: {},
            },
            enums: {
                origemCota
            },
            indeterminate: false,
            selectedAll: false,
            columns: [
                { key: 'procedimentoCodigo', label: 'Procedimento', class: 'col-min text-center', sortable: true },
                { key: 'exameNome', label: 'Exame', sortable: true },
                { key: 'prestadorNome', label: 'Prestador', sortable: true, fixed: true },
                { key: 'unidadeNome', label: 'Unidade', sortable: true, fixed: true },
                { key: 'localAtendimentoNome', label: 'Local de atendimento', class: 'col-min text-center', sortable: true },
                { key: 'cotaValor', label: 'Valor da cota', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'valorConsumido', label: 'Valor consumido', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'cotaQuantidade', label: 'Quantidade da cota', class: 'col-min text-center', sortable: true, fixed: true },
                { key: 'quantidadeConsumida', label: 'Quantidade consumida', class: 'col-min text-center', sortable: true, fixed: true },
            ],
            selectedCols: [],
        }
    },
    mixins: [repository, error, object, formats, data, file, stringCase],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.model.periodo = this.data.form.periodo
            this.tiposCota = this.data.form.tiposCota
            this.totais = this.clone(this.data.model.totais)
            this.model.tipoCotaId = this.enums.origemCota.EXAME

            this.loadFields()
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioCotaConsumo(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioCotaConsumo(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            if (this.model.tipoCotaId) {

                data.tipoCotaNome = this.tiposCota.find(t => t.id == this.model.tipoCotaId).nome
            }

            if (this.model.exameId) {

                data.exameNome = this.agendamentoExame.find(u => u.id == this.model.exameId).nome
            }

            if (data.unidadeId) {

                data.unidadeNome = this.unidadeSolicitante.find(u => u.unidadeId == data.unidadeId).nomeFantasia
            }

            if (data.infraestruturaId) {

                data.infraestruturaNome = this.infraestruturas.find(u => u.id == data.infraestruturaId).nome
            }

            if (data.laboratorioId) {

                data.laboratorioNome = this.unidadeLaboratorio.find(u => u.unidadeId == data.laboratorioId).nomeFantasia
            }

            if (data.laboratorioInfraestruturaId) {

                data.laboratorioInfraestruturaNome = this.laboratorioInfraestruturas.find(u => u.id == data.laboratorioInfraestruturaId).nome
            }

            this.formateDates(data.periodo, ['start', 'end'], 'YYYY-MM-DD')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        search() {

            this.setLoading()

            this.repositories.exame.list(this.getSearchParams())
                .then(({ data }) => {

                    this.itemsNotSave = data.model.cotas
                    this.totais = data.model.totais
                 })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getSearchParams() {

            const params = this.clone(this.model)

            this.formateDates(params.periodo, ['start', 'end'], 'YYYY-MM-DD')

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetForm() {

            this.model.tipoCotaId = null
            this.model.exameId = null
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.laboratorioId = null
            this.model.laboratorioInfraestruturaId = null
            this.agendamentoExame = []
            this.unidadeSolicitante = []
            this.unidadeLaboratorio = []
            this.selectedCols = []
            this.setListagem()

            this.setDefaultValues()

            this.$nextTick(() => this.$refs.Form.resetValidator())
        },
        setDefaultValues() {

            this.model.periodo.start = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
            this.model.periodo.end = moment(this.data.form.dataAtual, 'DD/MM/YYYY').toDate()
        },
        setListagem() {

            this.itemsNotSave = []
            this.totais = this.clone(this.data.model.totais)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.filter(item => item.disabled !== true).map(c => c.key) : []
        },
        getTotalRowClass(item) {

            return item && item.prestadorNome === 'TOTAL GERAL' ? 'font-weight-bold' : ''
        },
        loadFields() {

            this.setLoading()
            this.setListagem()

            this.repositories.exame.loadFields(this.getSearchParams())
                .then(({ data }) => {

                    this.loadUnidadeSolicitante(data.model.unidades)
                    this.loadLaboratorio(data.model.laboratorios)
                    this.loadExame(data.model.exames)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadUnidadeSolicitante(unidades) {

            const unidade = []

            unidades.map(infraestrutura => {

                const unidadeInserida = unidade.find(u => u.unidadeId === infraestrutura.id)

                if (unidadeInserida) {

                    let verifyUnidade = unidadeInserida.infraestruturas.find(u => u.id ===
                        infraestrutura.infraestruturaId)

                    if (!verifyUnidade) {

                        unidadeInserida.infraestruturas.push({

                            id: infraestrutura.infraestruturaId,
                            nome: infraestrutura.infraestruturaNome,
                            principal: infraestrutura.principal,
                        })
                    }
                } else {

                    unidade.push({

                        unidadeId: infraestrutura.id,
                        nomeFantasia: infraestrutura.nome,
                        infraestruturas: [
                            {
                                id: infraestrutura.infraestruturaId,
                                nome: infraestrutura.infraestruturaNome,
                                principal: infraestrutura.principal,
                            }
                        ]
                    })
                }
            })

            this.unidadeSolicitante = unidade
        },
        loadLaboratorio(laboratorios) {

            const laboratorio = []

            laboratorios.map(infraestrutura => {

                const laboratorioInserida = laboratorio.find(u => u.unidadeId === infraestrutura.id)

                if (laboratorioInserida) {

                    let verifyUnidade = laboratorioInserida.infraestruturas.find(u => u.id ===
                        infraestrutura.infraestruturaId)

                    if (!verifyUnidade) {

                        laboratorioInserida.infraestruturas.push({

                            id: infraestrutura.infraestruturaId,
                            nome: infraestrutura.infraestruturaNome,
                            principal: infraestrutura.principal,
                        })
                    }

                } else {

                    laboratorio.push({

                        unidadeId: infraestrutura.id,
                        nomeFantasia: infraestrutura.nome,
                        infraestruturas: [
                            {
                                id: infraestrutura.infraestruturaId,
                                nome: infraestrutura.infraestruturaNome,
                                principal: infraestrutura.principal,
                            }
                        ]
                    })
                }
            })

            this.unidadeLaboratorio = laboratorio
        },
        loadExame(exames) {

            const exameCadastrado = []

            exames.map(exame => {

                const exameInserido = exameCadastrado.find(u => u.id == exame.id)

                if (!exameInserido) {

                    exameCadastrado.push({

                        id: exame.id,
                        nome: exame.nome,
                    })
                }
            })

            if (exameCadastrado) {

                exameCadastrado.sort((a,b) => (a.nome > b.nome) ? 1 : ((b.nome > a.nome) ? -1 : 0));
            }

            this.agendamentoExame = exameCadastrado

            let exameSelecionado = this.agendamentoExame.find(u => u.id == this.model.exameId)

            if (!exameSelecionado) {

                this.model.exameId = null
            }
        },
        handleInfraestrutura(infraestruturaColetaPrincipal) {

            if (infraestruturaColetaPrincipal.find(u => u.principal)) {

                this.model.infraestruturaId = infraestruturaColetaPrincipal.find(u => u.principal).id
            }
        },
        handleLaboratorioInfraestrutura(infraestruturaLaboratorioPrincipal) {

            if (infraestruturaLaboratorioPrincipal.find(u => u.principal)) {

                this.model.laboratorioInfraestruturaId = infraestruturaLaboratorioPrincipal.find(u => u.principal).id
            }
        },
        handleTipoCota() {

            this.model.exameId = null
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.laboratorioId = null
            this.model.laboratorioInfraestruturaId = null
            this.agendamentoExame = []
            this.unidadeSolicitante = []
            this.unidadeLaboratorio = []
            this.setListagem()

            if (this.model.tipoCotaId) {

                this.loadFields()
            }
        },
        handlePeriodo() {

            this.model.exameId = null
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.laboratorioId = null
            this.model.laboratorioInfraestruturaId = null
            this.loadFields()
        }
    },
    filters: {
        ...format,
    },
    props: {
        data: { type: Object, required: true }
    },
    watch: {
        selectedCols(newValue) {

        this.selectedAll = newValue.length > 0
            && newValue.length == this.optionalColumns.filter(item => item.disabled !== true).length

        this.indeterminate = newValue.length > 0
            && newValue.length != this.optionalColumns.filter(item => item.disabled !== true).length
        },
        'model.periodo.start'() {

            if (this.model.periodo.start && this.model.periodo.end && this.model.tipoCotaId) {

                this.handlePeriodo()
            }
        },
        'model.periodo.end'() {

           if (this.model.periodo.start && this.model.periodo.end && this.model.tipoCotaId) {

                this.handlePeriodo()
            }
        },
        'model.unidadeId'() {

            this.model.infraestruturaId = null

            if (this.model.unidadeId) {

                let infraestruturaPrincipal = this.unidadeSolicitante.find(u => u.unidadeId == this.model.unidadeId).infraestruturas

                this.handleInfraestrutura(infraestruturaPrincipal)
            }
        },
        'model.laboratorioId'() {

            this.model.laboratorioInfraestruturaId = null

            if (this.model.laboratorioId) {

                let infraestruturaPrincipal = this.unidadeLaboratorio.find(u => u.unidadeId == this.model.laboratorioId).infraestruturas

                this.handleLaboratorioInfraestrutura(infraestruturaPrincipal)
            }
        },
    }
}
</script>

<style scoped>
.table-th {
    background: #f1f1f1;
    padding: 0 10px;
    height: 35px;
    line-height: 35px;
    font-weight: 600;
    border-top: 1px solid #dee2e6;
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
}
.table-td {
    background: #f1f1f1;
    padding: 0 10px;
    height: 35px;
    border-top: 1px solid #ccc;
    font-weight: 600;
}
.table-border-top {
    border-top: 0;
    height: 30px;
    padding: 0;
}
.table-agendamento >>> .col-width {
    width: 16%;
}
.coleta {
    background: #e9e9e9;
}
</style>
